import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../api";
import { useAuth } from "../auth/AuthContext";
import { PlanBadge, RoleBadge } from "../components/PlanBadge";
import { Skeleton, SkeletonStatGrid } from "../components/Loaders";
import { BentoCard, EmptyState, MetricCard } from "../components/Visual";
import { Icon, type IconName } from "../components/Icons";
import { DeckCardArt } from "./SavedDecks";
import type { QueueStatus, SavedDeckSummary } from "../types";

interface QuickAction {
  to: string;
  icon: IconName;
  title: string;
  body: string;
  variant?: "primary" | "ghost";
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    to: "/build",
    icon: "wand",
    title: "Build a deck",
    body: "Describe a strategy in plain English and get a legal, playable decklist with rationale and sideboard.",
    variant: "primary",
  },
  {
    to: "/analyze",
    icon: "chart",
    title: "Analyze a deck",
    body: "Paste an Arena export. See curve, pips, mana sources, and AI commentary on swaps.",
  },
  {
    to: "/collection",
    icon: "archive",
    title: "Tune with collection",
    body: "Import owned cards, cap rare spend, and constrain builds around what you can sleeve up.",
  },
];

export function Dashboard() {
  const { firebaseUser, profile } = useAuth();
  const name =
    profile?.displayName?.trim() ||
    firebaseUser?.displayName?.trim() ||
    (profile?.email ?? firebaseUser?.email ?? "").split("@")[0] ||
    "brewer";

  const isPlus = profile?.plan === "plus";
  const isPro = profile?.plan === "pro" || isPlus;
  const used = profile?.buildsToday ?? 0;
  const cap = profile?.dailyCap;
  const remaining = profile?.buildsRemaining;
  const usageLimit = cap ?? 3;
  const usageRatio = isPro ? 1 : Math.min(used / usageLimit, 1);
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(profile?.queue ?? null);

  const [recentDecks, setRecentDecks] = useState<SavedDeckSummary[] | null>(null);
  const [decksError, setDecksError] = useState<string | null>(null);
  useEffect(() => {
    if (!firebaseUser) return;
    let cancelled = false;
    apiGet<SavedDeckSummary[]>("/api/decks?limit=4")
      .then((d) => {
        if (!cancelled) setRecentDecks(d);
      })
      .catch((e) => {
        if (!cancelled) setDecksError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [firebaseUser]);

  useEffect(() => {
    setQueueStatus(profile?.queue ?? null);
  }, [profile?.queue]);

  useEffect(() => {
    if (!firebaseUser) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const snap = await apiGet<QueueStatus>("/api/queue/status");
        if (!cancelled) setQueueStatus(snap);
      } catch {
        // Keep dashboard usable even if queue endpoint momentarily fails.
      }
    };
    void tick();
    const id = window.setInterval(() => {
      void tick();
    }, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [firebaseUser]);

  const queueLabel = queueStatus
    ? queueStatus.status === "queued" && queueStatus.position
      ? `Pos ${queueStatus.position} · ${queueStatus.waiting} waiting`
      : queueStatus.status === "active"
      ? `Running · ${queueStatus.active}/${queueStatus.capacity} active`
      : `${queueStatus.waiting} waiting · ${queueStatus.active}/${queueStatus.capacity} active`
    : (isPro ? "Priority" : "Standard");

  return (
    <main className="page dashboard-page">
      <section className="dashboard-hero glass-strong dashboard-hero--wild">
        <div className="dashboard-hero-text">
          <span className="eyebrow">
            <span className="dot" />
            {isPlus ? "Plus command center" : isPro ? "Unlimited command center" : "Free command center"}
          </span>
          <h1>
            Welcome back,
            <br />
            <span className="grad">{name}.</span>
          </h1>
          <p className="muted">
            Your brewing cockpit is live. Jump into generation, inspect a list,
            or tighten the card pool before the next build.
          </p>
          <div className="cta dashboard-hero-actions">
            <Link to="/build" className="topbar-btn primary">Build a new deck</Link>
            <Link to="/analyze" className="topbar-btn">Analyze a list</Link>
          </div>
        </div>
        <div className="dashboard-status-card">
          <div className="dashboard-hero-pill">
            <PlanBadge plan={profile?.plan} />
            <RoleBadge role={profile?.role} />
          </div>
          <div className="dashboard-status-main">
            <div className="dashboard-usage-meter">
              <div className="dashboard-usage-title">Deck Builds</div>
              <div className="dashboard-usage-value" aria-live="polite">
                {isPro ? "∞ / ∞" : `${used} / ${usageLimit}`}
              </div>
              <div
                className="dashboard-usage-track"
                role="progressbar"
                aria-label="Build usage today"
                aria-valuemin={0}
                aria-valuemax={isPro ? undefined : usageLimit}
                aria-valuenow={isPro ? undefined : Math.min(used, usageLimit)}
              >
                <span style={{ width: `${Math.max(6, Math.round(usageRatio * 100))}%` }} />
              </div>
            </div>
            <p>
              {queueStatus?.status === "queued" && queueStatus.position
                ? `You're queued at position ${queueStatus.position}.`
                : isPro
                ? "Unlimited active: queue always open."
                : remaining != null && remaining > 0
                ? `${remaining} remaining before midnight ET reset.`
                : "Daily cap reached. Pro removes the ceiling."}
            </p>
          </div>
          <div className="dashboard-status-grid">
            <div>
              <span>Queue</span>
              <strong>{queueLabel}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-metrics">
        <MetricCard label="Builds today" value={used} detail={cap != null ? `of ${cap}` : "used"} icon="bolt" />
        <MetricCard label="Plan" value={isPlus ? "Plus" : isPro ? "Unlimited" : "Free"} detail={isPro ? "Unlimited" : "Daily cap"} icon="crown" />
        <MetricCard label="Workspace" value="Live" detail="Builder ready" icon="activity" />
      </section>

      <section>
        <div className="section-head">
          <h2>Jump back in</h2>
          <Link to="/account" className="hint section-link">Account settings</Link>
        </div>
        <div className="bento-grid quick-grid">
          {QUICK_ACTIONS.map((a) => (
            <Link
              key={a.title + a.to}
              to={a.to}
              className="quick-card-link"
            >
              <BentoCard icon={a.icon} title={a.title} feature={a.variant === "primary"}>
                <p>{a.body}</p>
              </BentoCard>
            </Link>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-head">
          <h2>Recent decks</h2>
          <Link to="/decks" className="hint section-link">View all saved</Link>
        </div>
        {decksError && <div className="error-banner">{decksError}</div>}
        {recentDecks === null && !decksError ? (
          <div className="deck-grid">
            {Array.from({ length: 3 }).map((_, i) => (
              <div className="deck-card" key={i}>
                <Skeleton variant="title" width="70%" style={{ marginBottom: 10 }} />
                <Skeleton variant="text" width="50%" style={{ marginBottom: 6 }} />
                <Skeleton variant="text" width="40%" />
              </div>
            ))}
          </div>
        ) : recentDecks && recentDecks.length > 0 ? (
          <div className="dashboard-recent-grid">
            {recentDecks.map((deck) => (
              <RecentDeckCard key={deck.id} deck={deck} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="cards"
            title="No saved decks yet"
            body="After generating a deck, save it and it will show up here as your personal brewing shelf."
            action={<Link to="/build" className="topbar-btn primary">Build one now</Link>}
          />
        )}
      </section>

      <section className="dashboard-section">
        <div className="section-head">
          <h2>Your account</h2>
        </div>
        {profile ? (
          <div className="account-grid">
            <div className="stat-card">
              <h4>Email</h4>
              <div className="value small">{profile.email ?? firebaseUser?.email}</div>
            </div>
            <div className="stat-card">
              <h4>Plan</h4>
              <div className="value small">
                <PlanBadge plan={profile.plan} /> {isPlus ? "Unlimited Plus" : isPro ? "Unlimited builds" : `${cap ?? 3} builds/day`}
              </div>
            </div>
            <div className="stat-card">
              <h4>Builds used today</h4>
              <div className="value">
                {used}
                {cap != null && <span className="muted"> / {cap}</span>}
              </div>
              {!isPro && remaining != null && <div className="hint">{remaining} remaining. Resets at midnight ET.</div>}
            </div>
          </div>
        ) : (
          <SkeletonStatGrid count={3} />
        )}

        {profile && !isPro && (
          <div className="quota-banner quota-banner--foil">
            <span>Free plan active. Upgrade to Unlimited for unlimited builds and Bloomy access.</span>
            <Link to="/pricing">See plans</Link>
          </div>
        )}

        {profile?.isAdmin && (
          <div className="rationale-block admin-teaser">
            <h3>Admin tools</h3>
            <p className="hint">Manage users, promote admins, and refresh card data.</p>
            <div className="actions">
              <Link to="/admin" className="topbar-btn">Admin console</Link>
              <Link to="/admin/users" className="topbar-btn">Users</Link>
              <Link to="/admin/update" className="topbar-btn">Card data update</Link>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

function RecentDeckCard({ deck }: { deck: SavedDeckSummary }) {
  const colors = (deck.theme?.colors || []).join("") || "-";
  const main = deck.totals?.mainboard ?? 0;
  const side = deck.totals?.sideboard ?? 0;
  const updated = deck.updatedAt
    ? new Date(deck.updatedAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "Saved deck";

  return (
    <Link
      to={`/decks/${encodeURIComponent(deck.id)}`}
      className="dashboard-recent-card deck-card--clickable"
    >
      <DeckCardArt deck={deck} />
      <div className="dashboard-recent-body">
        <div className="dashboard-recent-kicker">
          <span>{deck.format || "Deck"}</span>
          {deck.published && <span className="deck-meta-pill public">Public</span>}
        </div>
        <h3>{deck.name}</h3>
        <p>{deck.theme?.archetype || deck.query || "No archetype yet"}</p>
        <div className="dashboard-recent-meta">
          <span className="colors">{colors}</span>
          <span>Main {main}</span>
          {side > 0 && <span>Side {side}</span>}
          <span>{updated}</span>
        </div>
        <span className="dashboard-recent-link">
          Open deck <Icon name="chevronRight" size={14} />
        </span>
      </div>
    </Link>
  );
}
